'use client'

import { CountdownTimer } from '@/components/common/count-down-timer'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TFlashSaleConfig, TFlashSaleTabStyle } from '@/types/collection'
import { FlashSale } from '@/types/flash-sale'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { CSSProperties } from 'react'

// Giá trị mặc định khi admin không cấu hình (giữ đúng thiết kế cũ)
const DEFAULT_STYLE = {
  background: { color_1: '#1D4729', color_2: '#143025' },
  title_color: '#FFFFFF',
  event_badge: {
    bg_color: '#A7342C',
    text_color: '#FFFFFF',
    border_color: '#FFE7CF',
  },
  tab_current: {
    bg_color: '#A7342C',
    text_color: '#FFFFFF',
    active_border_color: '#FFFFFF',
  },
  tab_next: {
    bg_color: '#FFE7CF',
    text_color: '#000000',
    active_border_color: '#000000',
  },
}

const getTabStyle = (
  style: TFlashSaleTabStyle | undefined,
  fallback: Required<TFlashSaleTabStyle>
): Required<TFlashSaleTabStyle> => ({
  bg_color: style?.bg_color || fallback.bg_color,
  text_color: style?.text_color || fallback.text_color,
  active_border_color:
    style?.active_border_color || fallback.active_border_color,
})

const getBackgroundStyle = (config?: TFlashSaleConfig): CSSProperties => {
  const bg = config?.background
  const colors = [bg?.color_1, bg?.color_2].filter(Boolean) as string[]

  // Có ảnh thì ưu tiên ảnh (màu 1 làm nền chờ ảnh load)
  if (bg?.image_desktop || bg?.image_mobile) {
    const desktop = bg.image_desktop || bg.image_mobile || ''
    const mobile = bg.image_mobile || bg.image_desktop || ''
    return {
      '--fs-bg-desktop': `url(${ImageHelper.getImageUrlCdn(desktop)})`,
      '--fs-bg-mobile': `url(${ImageHelper.getImageUrlCdn(mobile)})`,
      backgroundColor: colors[0],
    } as CSSProperties
  }

  // 2 màu = gradient, 1 màu = nền đơn sắc
  if (colors.length === 1) return { backgroundColor: colors[0] }

  const [from, to] =
    colors.length === 2
      ? colors
      : [DEFAULT_STYLE.background.color_1, DEFAULT_STYLE.background.color_2]
  return { backgroundImage: `linear-gradient(to left, ${from}, ${to})` }
}

type FlashSaleTabProps = {
  item: FlashSale
  index: number
  type: 'current' | 'next'
  title: string
  activeTab?: string
  setActiveTab?: (tab: string) => void
  tabStyle: Required<TFlashSaleTabStyle>
}

const FlashSaleTab = ({
  item,
  index,
  type,
  title,
  activeTab,
  setActiveTab,
  tabStyle,
}: FlashSaleTabProps) => {
  const itemId = `${type}-${index}`
  const isActive = activeTab === itemId

  return (
    <div
      className={cn(
        'cursor-pointer rounded-t-2xl px-3 py-2 text-center md:px-8',
        isActive ? 'border-b-2' : ''
      )}
      style={{
        backgroundColor: tabStyle.bg_color,
        color: tabStyle.text_color,
        borderColor: isActive ? tabStyle.active_border_color : undefined,
      }}
      onClick={() => setActiveTab && setActiveTab(itemId)}
    >
      <div className='text-xs font-normal md:text-sm'>{title}</div>
      <div className='text-base font-medium md:text-2xl'>
        {formatFlashSaleTime(item)}
      </div>
    </div>
  )
}

const formatFlashSaleTime = (item: FlashSale): string => {
  const startTime = dayjs(item.starts_at)
  const endTime = dayjs(item.ends_at)

  return `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`
}

interface FlashSaleListProps {
  current: FlashSale[]
  next: FlashSale[]
  activeTab?: string
  setActiveTab?: (tab: string) => void
  currentTabStyle: Required<TFlashSaleTabStyle>
  nextTabStyle: Required<TFlashSaleTabStyle>
}

const FlashSaleList = ({
  current,
  next,
  activeTab,
  setActiveTab,
  currentTabStyle,
  nextTabStyle,
}: FlashSaleListProps) => {
  const t = useTranslations()
  const handleItemClick = (id: string) => {
    if (setActiveTab) {
      setActiveTab(id)
    }
  }

  return (
    <div className='mt-1 flex gap-10'>
      {current.length > 0
        ? current.map((item, index) => (
          <div
            key={`current-${index}`}
            className='flex flex-col items-center font-criteria'
            onClick={() => handleItemClick(`current-${index}`)}
          >
            <FlashSaleTab
              item={item}
              index={index}
              type='current'
              title={t('flash_sale_hardcode.banner.current')}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tabStyle={currentTabStyle}
            />
          </div>
        ))
        : null}
      {next.length > 0
        ? next.map((item, index) => (
          <div
            key={`next-${index}`}
            className='flex flex-col items-center font-criteria'
            onClick={() => handleItemClick(`next-${index}`)}
          >
            <FlashSaleTab
              item={item}
              index={index}
              type='next'
              title={t('flash_sale_hardcode.banner.next')}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tabStyle={nextTabStyle}
            />
          </div>
        ))
        : null}
    </div>
  )
}

interface BannerCountDownProps {
  countDownTime: Date | null
  current?: FlashSale[]
  next?: FlashSale[]
  className?: string
  activeTab?: string
  setActiveTab?: (tab: string) => void
  config?: TFlashSaleConfig
}

export const BannerCountDown = ({
  countDownTime,
  current = [],
  next = [],
  className = '',
  activeTab = '',
  setActiveTab,
  config,
}: BannerCountDownProps) => {
  const t = useTranslations()

  const title = config?.title || t('flash_sale_hardcode.banner.title')
  const eventBadgeText =
    config?.event_badge_text || t('flash_sale_hardcode.banner.event_title')

  const titleColor = config?.title_color || DEFAULT_STYLE.title_color
  const eventBadge = {
    bg_color: config?.event_badge?.bg_color || DEFAULT_STYLE.event_badge.bg_color,
    text_color:
      config?.event_badge?.text_color || DEFAULT_STYLE.event_badge.text_color,
    border_color:
      config?.event_badge?.border_color ||
      DEFAULT_STYLE.event_badge.border_color,
  }
  const currentTabStyle = getTabStyle(
    config?.tab_current,
    DEFAULT_STYLE.tab_current
  )
  const nextTabStyle = getTabStyle(config?.tab_next, DEFAULT_STYLE.tab_next)

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-2xl bg-cover bg-center bg-[image:var(--fs-bg-mobile)] md:bg-[image:var(--fs-bg-desktop)]',
        className
      )}
      style={{ ...getBackgroundStyle(config), color: titleColor }}
    >
      {/* Desktop layout */}
      <div className='hidden items-center justify-between px-[120px] pt-4 md:flex'>
        <div className='flex items-center'>
          <h2 className='text-5xl font-bold'>{title}</h2>
        </div>

        <div className='flex items-center justify-center'>
          <div className='flex items-center space-x-2 font-criteria text-5xl font-bold'>
            {countDownTime && (
              <CountdownTimer expiryTimestamp={countDownTime} />
            )}
          </div>
        </div>

        <div className='flex items-center'>
          <div className='text-center'>
            <div
              className='mx-auto mb-6 w-fit rounded-full border px-6 py-2 font-criteria text-3xl font-medium uppercase'
              style={{
                backgroundColor: eventBadge.bg_color,
                color: eventBadge.text_color,
                borderColor: eventBadge.border_color,
              }}
            >
              {eventBadgeText}
            </div>
            <FlashSaleList
              current={current}
              next={next}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              currentTabStyle={currentTabStyle}
              nextTabStyle={nextTabStyle}
            />
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className='flex flex-col items-center px-4 pt-4 md:hidden'>
        <div className='mb-3'>
          <h2 className='text-2xl font-bold'>{title}</h2>
        </div>

        <div className='mb-4'>
          <div className='flex items-center space-x-2 text-4xl font-bold'>
            {countDownTime && (
              <CountdownTimer expiryTimestamp={countDownTime} />
            )}
          </div>
        </div>

        <div className='w-full'>
          <div className='grid w-full grid-cols-2 gap-2'>
            {/* Hiển thị các chương trình đang diễn ra */}
            {current.map((item, index) => (
              <FlashSaleTab
                key={`current-${index}`}
                item={item}
                index={index}
                type='current'
                title={t('flash_sale_hardcode.banner.current')}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabStyle={currentTabStyle}
              />
            ))}

            {/* Hiển thị các chương trình sắp diễn ra */}
            {next.map((item, index) => (
              <FlashSaleTab
                key={`next-${index}`}
                item={item}
                index={index}
                type='next'
                title={t('flash_sale_hardcode.banner.next')}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabStyle={nextTabStyle}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
